import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProjet, updateProjet } from "../services/api";
import ProjetEtapesV2 from "../components/ProjetEtapesV2";
import ProjetJalonsV2 from "../components/ProjetJalonsV2";
import ProjetFicheV2 from "../components/ProjetFicheV2";
import ProjetEquipeV2 from "../components/ProjetEquipeV2";
import ProjetDocumentsV2 from "../components/ProjetDocumentsV2";
import ConversationProjetV2 from "../components/ConversationProjetV2";
import {
  STATUTS_PROJET, LIBELLES_STATUT, LIBELLES_ROLE_PROJET, classeStatutProjet, formatDate, projetEnRetard, largeurJauge,
} from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet
//
// Refonte du module Projets : un projet est l'objectif d'un
// collaborateur (acteur principal), fixe et suivi par son chef.
// Route : /v2/projets/:id — ouverte depuis Mes projets, Mes etapes et
// la vue manager (briques 5 et 6). Visible par toute personne ayant un
// role sur le projet, et par les directeurs ; le backend controle
// (collaborateur_id) et renvoie les droits de l'appelant (mes_droits),
// qui pilotent l'affichage des actions.
//
// Onglets : Suivi (etapes, jalons), Fiche, Equipe, Documents,
// Conversation.
// ===================================================================

const ONGLETS = [
  { id: "suivi", libelle: "Suivi" },
  { id: "fiche", libelle: "Fiche" },
  { id: "equipe", libelle: "Équipe" },
  { id: "documents", libelle: "Documents" },
  { id: "conversation", libelle: "Conversation" },
];

function FicheProjetV2({ collaborateur }) {
  const { id } = useParams();
  const projetId = Number(id);
  const navigate = useNavigate();

  const [projet, setProjet] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState("suivi");
  const [statutEnCours, setStatutEnCours] = useState(false);
  const [messageStatut, setMessageStatut] = useState(null);

  const collaborateurId = collaborateur?.id;

  const charger = useCallback(() => {
    if (!collaborateurId) return Promise.resolve();
    return getProjet(projetId, collaborateurId)
      .then((p) => { setProjet(p); setErreur(null); })
      .catch((err) => setErreur(err.message));
  }, [projetId, collaborateurId]);

  useEffect(() => {
    setProjet(null);
    setOnglet("suivi");
    charger();
  }, [charger]);

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">Compte non configuré. Contactez votre manager.</div>
      </div>
    );
  }

  if (erreur && !projet) {
    return (
      <div className="v2-page v2-page-module">
        <button type="button" className="v2-btn v2-btn-secondaire v2-marge-bas" onClick={() => navigate(-1)}>Retour</button>
        <div className="v2-msg v2-msg-erreur">Projet non accessible : {erreur}</div>
      </div>
    );
  }

  if (!projet) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-chargement">Chargement du projet…</div>
      </div>
    );
  }

  const droits = projet.mes_droits || {};
  const roles = droits.roles || [];

  const changerStatut = async (statut) => {
    setStatutEnCours(true);
    setMessageStatut(null);
    try {
      await updateProjet(projet.id, { statut }, collaborateurId);
      await charger();
    } catch (err) {
      setMessageStatut(err.message);
    } finally {
      setStatutEnCours(false);
    }
  };

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-fiche-entete v2-projet-entete">
        <button type="button" className="v2-lien-ligne v2-projet-retour" onClick={() => navigate(-1)}>← Retour</button>

        <div className="v2-projet-titre-ligne">
          <h1 className="v2-module-titre">{projet.nom}</h1>
          <span className={classeStatutProjet(projet.statut)}>{LIBELLES_STATUT[projet.statut] || projet.statut}</span>
          {projetEnRetard(projet) && <span className="v2-statut v2-statut-danger">En retard</span>}
        </div>

        <div className="v2-fiche-meta">
          <span>Chef : {projet.chef_projet_nom || "—"}</span>
          <span className="v2-fiche-sep">·</span>
          <span>Acteur principal : {projet.acteur_principal_nom || "—"}</span>
          <span className="v2-fiche-sep">·</span>
          <span>{formatDate(projet.date_debut)} → {formatDate(projet.date_fin_prevue)}</span>
          {roles.length > 0 && (
            <>
              <span className="v2-fiche-sep">·</span>
              <span>Mon rôle : {roles.map((r) => LIBELLES_ROLE_PROJET[r] || r).join(", ")}</span>
            </>
          )}
        </div>

        <div className="v2-projet-synthese">
          <div className="v2-projet-jauge v2-projet-jauge-large">
            <span className="v2-barres-piste">
              <span className="v2-barres-valeur v2-ton-fort" style={{ width: largeurJauge(projet.avancement) }} />
            </span>
            <span className="v2-projet-jauge-valeur">{projet.avancement || 0} %</span>
          </div>
          {droits.peut_gerer && (
            <div className="v2-champ v2-champ-court">
              <label className="v2-label" htmlFor="projet-statut">Statut</label>
              <select id="projet-statut" value={projet.statut} disabled={statutEnCours}
                onChange={(e) => changerStatut(e.target.value)}>
                {STATUTS_PROJET.map((s) => <option key={s} value={s}>{LIBELLES_STATUT[s]}</option>)}
              </select>
            </div>
          )}
        </div>
        {messageStatut && <div className="v2-msg v2-msg-erreur">{messageStatut}</div>}
      </header>

      <div className="v2-bascule v2-projet-onglets" role="tablist" aria-label="Sections du projet">
        {ONGLETS.map((o) => (
          <button key={o.id} type="button" role="tab" aria-selected={onglet === o.id}
            className={`v2-bascule-choix${onglet === o.id ? " actif" : ""}`} onClick={() => setOnglet(o.id)}>
            {o.libelle}
          </button>
        ))}
      </div>

      {onglet === "suivi" && (
        <div className="v2-projet-suivi">
          <ProjetEtapesV2 projetId={projet.id} collaborateurId={collaborateurId} droits={droits} onModifie={charger} />
          <ProjetJalonsV2 projetId={projet.id} collaborateurId={collaborateurId} droits={droits} />
        </div>
      )}
      {onglet === "fiche" && (
        <ProjetFicheV2 projet={projet} collaborateurId={collaborateurId} droits={droits} onModifie={charger} />
      )}
      {onglet === "equipe" && (
        <ProjetEquipeV2 projet={projet} collaborateurId={collaborateurId} droits={droits} onModifie={charger} />
      )}
      {onglet === "documents" && (
        <ProjetDocumentsV2 projetId={projet.id} collaborateurId={collaborateurId} droits={droits} />
      )}
      {onglet === "conversation" && (
        <section className="v2-panneau v2-projet-bloc">
          <div className="v2-bloc-entete">
            <h2 className="v2-bloc-titre">Conversation</h2>
          </div>
          <ConversationProjetV2 projetId={projet.id} collaborateurId={collaborateurId} />
        </section>
      )}
    </div>
  );
}

export default FicheProjetV2;
